<template>
  <div class="date_range_picker" @click.stop>
    <div v-if="timezone" class="date_picker_timezone">
      <span>{{ $t('common.timezone') }}</span>
      <span class="value">{{ timezone }}</span>
    </div>
    <div class="date_range_picker_input--wrap">
      <span class="date_range_picker_input">
        <i class="icon icon--calendar-input"></i><input type="text" :value="startDateStr" readonly></span>
      <span> ~ </span>
      <span class="date_range_picker_input">
        <i class="icon icon--calendar-input"></i><input type="text" :value="endDateStr" readonly></span>
    </div>
    <div class="calendar clearfix">
      <month-calendar v-for="index in 2" :key="index"
        :class="index === 1 ? 'left' : 'right'"
        :uiType="index === 1 ? 'left' : 'right'"
        :htmldiff="htmldiff"
        :aid="aid"
        :device="device"
        :monthStr="tempMonthStr"
        :minDate="minDate"
        :maxDate="computedMaxDate"
        :dateRange="tempDateRange"
        :previewEndDate="previewEndDate"
        @onMonthAdd="onMonthAdd"
        @onDateClick="onDateClick"
        @onRangePreviewChange="onRangePreviewChange"/>
    </div>
    <div class="clearfix">
      <p v-if="pickerType === 'reportPeriod'" class="notice">
        <span v-html="$t('heatmap.datePeriodNotice')"></span>
      </p>
      <p class="action">
        <ui-button @click="onCancelClick">
          {{ $t('common.cancel') }}
        </ui-button>
        <ui-button primary
          :disabled="!canApply"
          @click="onConfirmClick">
          {{ $t('common.apply') }}
        </ui-button>
      </p>
    </div>
  </div>
</template>

<script>
import { format, parse, addMonths, isBefore, addDays } from 'date-fns'
import { getLocaleDateStr } from '@/libs/utils'
import MonthCalendar from './MonthCalendar'
import UiButton from '@/components/ui/UiButton'

export default {
  name: 'DateRangePicker',
  components: {
    MonthCalendar,
    UiButton
  },
  props: {
    pickerType: {
      type: String,
      default: 'reportPeriod'
    },
    timezone: {
      type: String,
      required: false
    },
    minDate: {
      type: Date,
      required: false
    },
    maxDate: {
      type: Date,
      required: false
    },
    dateRange: {
      type: Object,
      default () {
        return {
          startDate: null,
          endDate: null
        }
      }
    },
    monthStr: {
      type: String,
      required: false
    },
    maxDays: {
      type: Number,
      default: 0
    },
    htmldiff: {
      type: Boolean,
      default: false,
      required: false
    },
    aid: {
      type: String,
      required: false
    },
    device: {
      type: String,
      default: 'desktop',
      required: false
    }
  },
  data () {
    return {
      tempMonthStr: null,
      tempDateRange: null,
      previewEndDate: null
    }
  },
  computed: {
    startDateStr () {
      if (this.tempDateRange.startDate) {
        return getLocaleDateStr(this.tempDateRange.startDate)
      } else {
        return this.$t('common.placeHolder.periodStart')
      }
    },
    endDateStr () {
      if (this.tempDateRange.endDate) {
        return getLocaleDateStr(this.tempDateRange.endDate)
      } else {
        return this.$t('common.placeHolder.periodEnd')
      }
    },
    canApply () {
      return this.tempDateRange.startDate && this.tempDateRange.endDate
    },
    computedMaxDate () {
      if (this.maxDays && this.tempDateRange.startDate && !this.tempDateRange.endDate) {
        const newEndDate = addDays(this.tempDateRange.startDate, this.maxDays - 1)

        if (isBefore(newEndDate, this.maxDate)) {
          return newEndDate
        }
      }
      return this.maxDate
    }
  },
  created () {
    if (this.monthStr) {
      this.tempMonthStr = this.monthStr
    } else {
      const range = this.dateRange

      if (range && range.startDate && range.endDate) {
        this.tempMonthStr = format(range.endDate, 'YYYY-MM')
      } else {
        this.tempMonthStr = format(new Date(), 'YYYY-MM')
      }
    }
    this.setTempDateRange()

    this.closeFn = () => {
      this.onCancelClick()
    }
    document.body.addEventListener('click', this.closeFn, false)
  },
  destroyed () {
    document.body.removeEventListener('click', this.closeFn)
  },
  methods: {
    setTempDateRange () {
      this.tempDateRange = {
        startDate: this.dateRange.startDate,
        endDate: this.dateRange.endDate
      }
    },
    onMonthAdd (months) {
      let monthDate = parse(this.tempMonthStr)
      monthDate = addMonths(monthDate, months)
      this.tempMonthStr = format(monthDate, 'YYYY-MM-DD')
    },
    onRangePreviewChange (type, date) {
      this.previewEndDate = date
    },
    onDateClick (type, date) {
      const range = this.tempDateRange

      if (!range.startDate || range.endDate || (!range.endDate && isBefore(date, range.startDate))) {
        // range with startDate only
        range.startDate = date
        range.endDate = null
        this.previewEndDate = null
      } else {
        // range with startDate and endDate
        range.endDate = date
        this.previewEndDate = null
      }
    },
    onCancelClick () {
      this.$emit('onRangeCancel')
    },
    onConfirmClick () {
      const range = this.tempDateRange

      if (!range.startDate || !range.endDate) {
        return
      }
      this.tempDateRange.startDate = format(this.tempDateRange.startDate, 'YYYY-MM-DD')
      this.tempDateRange.endDate = format(this.tempDateRange.endDate, 'YYYY-MM-DD')

      this.$emit('onRangeChange', {
        range: this.tempDateRange,
        monthStr: this.tempMonthStr
      })
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
</style>
